import React, { useEffect } from 'react';
import { Modal, Button, Form, Col } from 'react-bootstrap';
import { ResponseEstagioEventoDto, CreateEstagioEventoDto } from '@/api.types';
import { useForm } from 'react-hook-form';

interface AdicionarEventoEstagioModalProps {
  show: boolean;
  handleHide: () => void;
  initialData?: ResponseEstagioEventoDto | null;
  idEstagio: number;
  onSave: () => void;
}

const TIPOS_EVENTO = [
  { value: 1, name: 'Férias' },
  { value: 2, name: 'Folga' },
  { value: 3, name: 'Abono' },
];

const AdicionarEventoEstagioModal: React.FC<AdicionarEventoEstagioModalProps> = ({
  show,
  handleHide,
  initialData,
  idEstagio,
  onSave,
}) => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateEstagioEventoDto>({
    defaultValues: {
      estagio_id: idEstagio,
    },
  });

  useEffect(() => {
    if (initialData) {
      reset({
        id: initialData.id,
        estagio_id: initialData.estagio_id,
        tipo_evento: initialData.tipo_evento as 1 | 2 | 3,
        data_inicio: initialData.data_inicio.split('T')[0],
        data_fim: initialData.data_fim.split('T')[0],
        observacao: initialData.observacao || '',
      });
    } else {
      reset({
        estagio_id: idEstagio,
        tipo_evento: 1,
        data_inicio: '',
        data_fim: '',
        observacao: '',
      });
    }
  }, [initialData, idEstagio, reset]);

  const onSubmit = async (data: CreateEstagioEventoDto) => {
    try {
      const { criarEventoEstagio, atualizarEventoEstagio } = await import('@/modules/people/estagio.service');

      if (initialData?.id) {
        await atualizarEventoEstagio(initialData.id, data);
      } else {
        await criarEventoEstagio(data);
      }

      onSave();
      handleHide();
    } catch (error: any) {
      const { toast } = await import('react-toastify');
      toast.error(error.message || 'Erro ao salvar evento.');
    }
  };

  return (
    <Modal show={show} onHide={handleHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {initialData?.id ? 'Editar ' : 'Adicionar '} Evento de Estágio
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Group as={Col} md="12" className="mb-3">
            <label htmlFor="tipo_evento" className="form-label">
              Tipo de Evento: <span className="text-danger">*</span>
            </label>
            <select
              id="tipo_evento"
              className={`form-select ${errors.tipo_evento ? 'is-invalid' : ''}`}
              {...register('tipo_evento', { required: 'Selecione o tipo de evento' })}
            >
              {TIPOS_EVENTO.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.name}
                </option>
              ))}
            </select>
            {errors.tipo_evento && (
              <div className="invalid-feedback">{errors.tipo_evento.message}</div>
            )}
          </Form.Group>

          <Form.Group as={Col} md="6" className="mb-3">
            <label htmlFor="data_inicio" className="form-label">
              Data Início: <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              id="data_inicio"
              className={`form-control ${errors.data_inicio ? 'is-invalid' : ''}`}
              {...register('data_inicio', { required: 'Informe a data de início' })}
            />
            {errors.data_inicio && (
              <div className="invalid-feedback">{errors.data_inicio.message}</div>
            )}
          </Form.Group>

          <Form.Group as={Col} md="6" className="mb-3">
            <label htmlFor="data_fim" className="form-label">
              Data Fim: <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              id="data_fim"
              className={`form-control ${errors.data_fim ? 'is-invalid' : ''}`}
              {...register('data_fim', { required: 'Informe a data de fim' })}
            />
            {errors.data_fim && (
              <div className="invalid-feedback">{errors.data_fim.message}</div>
            )}
          </Form.Group>

          <Form.Group as={Col} md="12" className="mb-3">
            <label htmlFor="observacao" className="form-label">
              Observação:
            </label>
            <textarea
              id="observacao"
              className="form-control"
              rows={3}
              {...register('observacao')}
              placeholder="Observações sobre o evento..."
            />
          </Form.Group>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleHide}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary">
              Salvar
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AdicionarEventoEstagioModal;

